import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  schema?: Record<string, unknown>;
  children?: ReactNode;
}

const SITE_NAME = "久久视频";
const BASE_URL = "https://www.jiujiu-video.com";
const DEFAULT_DESCRIPTION =
  "久久视频聚合蜜桃视频、樱桃视频、蘑菇视频、茄子视频等泛娱乐内容，免费在线看综艺、动漫、短视频、搞笑创意视频，轻松刷不停。";
const DEFAULT_KEYWORDS =
  "久久视频,蜜桃视频,樱桃视频,蘑菇视频,茄子视频,综艺,动漫,短视频,创意视频,搞笑视频,免费在线观看,芒果视频";
const DEFAULT_OG_IMAGE =
  "https://readdy.ai/api/search-image?query=Jiujiu%20video%20entertainment%20platform%20brand%20banner%2C%20modern%20minimalist%20design%2C%20vibrant%20warm%20colors%2C%20playful%20atmosphere%2C%20clean%20background&width=1200&height=630&seq=og-image-default&orientation=landscape";

export default function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords = DEFAULT_KEYWORDS,
  canonicalUrl,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  schema,
  children,
}: SEOProps) {
  const location = useLocation();
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME}-蜜桃视频樱桃视频综艺动漫短视频 | 免费在线看搞笑创意视频大全`;
  const fullCanonical = canonicalUrl || `${BASE_URL}${location.pathname}`;

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "久久视频",
    url: BASE_URL,
    description: DEFAULT_DESCRIPTION,
    sameAs: [],
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: BASE_URL,
    description: DEFAULT_DESCRIPTION,
    potentialAction: {
      "@type": "SearchAction",
      target: `${BASE_URL}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };

  const webpageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: fullTitle,
    description: description,
    url: fullCanonical,
    isPartOf: {
      "@type": "WebSite",
      name: SITE_NAME,
      url: BASE_URL,
    },
  };

  const allSchemas = [organizationSchema, websiteSchema, webpageSchema];
  if (schema) {
    allSchemas.push(schema);
  }

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={fullCanonical} />

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="zh_CN" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      <script type="application/ld+json">
        {JSON.stringify(allSchemas)}
      </script>
      {children}
    </>
  );
}